import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useWindowSize } from "@reach/window-size";
import { ReactComponent as CloseIcon } from "../../assets/icons/notifications-close.svg";
import { ReactComponent as MarkAllReadIcon } from "../../assets/icons/mark-all-read.svg";
import { ReactComponent as SettingsIcon } from "../../assets/icons/notifications-settings.svg";
import {
  invalidateNotificationsQueries,
  useNotifications,
  useUnreadNotificationsCount,
} from "../../app/queries/notifications";
import { appRoutePaths } from "../../app/router/utils";
import Loader from "../common/Loader";
import EmptyNotifications from "./EmptyNotifications";
import NotificationCard from "./NotificationCard";
import NotificationFilters from "./NotificationFilters";
import NotificationPreferencesModal from "./NotificationPreferencesModal";
import NotificationReceiptModal from "./NotificationReceiptModal";
import {
  groupNotificationsByDate,
  mapApiNotifications,
} from "./mapNotification";
import {
  Content,
  DateGroupLabel,
  Footer,
  Header,
  HeaderActions,
  IconButton,
  Sheet,
  SheetInner,
  Title,
  ToastBanner,
  ViewAllButton,
} from "./styles";
import { NotificationFilter } from "./types";
import { useNotificationActions } from "./useNotificationActions";

type Props = {
  open: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
};

const NotificationsCentre = ({ open, onClose, onUnreadCountChange }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { width } = useWindowSize();
  const [filter, setFilter] = useState<NotificationFilter>("all");
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const onlyUnread = filter === "unread";

  useEffect(() => {
    if (open) {
      invalidateNotificationsQueries(queryClient);
    }
  }, [open, queryClient]);

  const { data: unreadCountData } = useUnreadNotificationsCount(open);
  const { data: allNotificationsMeta } = useNotifications({
    page: 0,
    size: 1,
    onlyUnread: false,
    enabled: open,
  });
  const { data, isLoading, isFetching } = useNotifications({
    page: 0,
    size: 20,
    onlyUnread,
    enabled: open,
  });

  const {
    showMarkedToast,
    isMarkingAllAsRead,
    handleMarkAllAsRead,
    handleNotificationClick,
    handleViewTransaction,
    handleDownloadReceipt,
    receiptOpen,
    receipt,
    receiptError,
    receiptLoading,
    closeReceipt,
  } = useNotificationActions({
    onBeforeNavigate: onClose,
  });

  const notifications = useMemo(
    () => mapApiNotifications(data?.content ?? []),
    [data?.content]
  );

  const allCount = allNotificationsMeta?.totalElements ?? 0;
  const unreadCount = unreadCountData ?? 0;

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(notifications),
    [notifications]
  );

  const showEmptyState = !isLoading && notifications.length === 0;

  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  const handleViewAll = () => {
    onClose();
    navigate(appRoutePaths.notifications);
  };

  return (
    <>
      <Sheet
        placement="right"
        open={open}
        onClose={onClose}
        closable={false}
        title={null}
        width={width < 531 ? "100vw" : 420}
        destroyOnClose={false}
      >
        <SheetInner>
          <Header>
            <Title>Notifications</Title>
            <HeaderActions>
              <IconButton
                type="button"
                aria-label="Mark all as read"
                onClick={() => handleMarkAllAsRead(unreadCount)}
                disabled={unreadCount === 0 || isMarkingAllAsRead}
              >
                <MarkAllReadIcon />
              </IconButton>
              <IconButton
                type="button"
                aria-label="Notification preferences"
                onClick={() => setPreferencesOpen(true)}
              >
                <SettingsIcon />
              </IconButton>
              <IconButton
                type="button"
                aria-label="Close notifications"
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
            </HeaderActions>
          </Header>

          <NotificationFilters
            activeFilter={filter}
            allCount={allCount}
            unreadCount={unreadCount}
            onChange={setFilter}
          />

          <Content>
            {isLoading || isFetching ? (
              <Loader isLoading isComponentType />
            ) : showEmptyState ? (
              <EmptyNotifications />
            ) : (
              groupedNotifications.map((group) => (
                <React.Fragment key={group.label}>
                  <DateGroupLabel>{group.label}</DateGroupLabel>
                  {group.items.map((notification) => (
                    <NotificationCard
                      key={notification.id}
                      notification={notification}
                      onClick={handleNotificationClick}
                      onViewTransaction={handleViewTransaction}
                      onDownloadReceipt={handleDownloadReceipt}
                    />
                  ))}
                </React.Fragment>
              ))
            )}
          </Content>

          {!showEmptyState && !isLoading && (
            <Footer>
              <ViewAllButton type="button" onClick={handleViewAll}>
                View all notifications
              </ViewAllButton>
            </Footer>
          )}
        </SheetInner>
      </Sheet>

      <NotificationReceiptModal
        open={receiptOpen}
        loading={receiptLoading}
        error={receiptError}
        receipt={receipt}
        onClose={closeReceipt}
      />

      <NotificationPreferencesModal
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />

      {showMarkedToast && (
        <ToastBanner role="status">
          All notifications marked as read
        </ToastBanner>
      )}
    </>
  );
};

export default NotificationsCentre;
